import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';

import { AdminApiClient } from 'configurations/api-client';
import { HANDOFF_TARGETS } from 'constants/admin/handoff-targets';

export const HANDOFF_SESSIONS_PATH = '/handoff_sessions';
export const HANDOFF_CODES_PATH = '/handoff_codes';

const HANDOFF_PARAMS = ['code', 'redirectTo'];

const readHandoffQuery = () => {
  const params = new URLSearchParams(window.location.search);

  return { code: params.get('code'), redirectTo: params.get('redirectTo') };
};

// The code is single-use: read it once, then drop it (and only it) from the address bar and browser history,
// keeping the page's own query (a preview's pageId, the language) in place.
export const useHandoffQuery = () => {
  const [handoff] = useState(readHandoffQuery);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    if (!HANDOFF_PARAMS.some((key) => params.has(key))) return;

    HANDOFF_PARAMS.forEach((key) => params.delete(key));
    const search = params.toString();
    window.history.replaceState(
      window.history.state,
      '',
      `${window.location.pathname}${search ? `?${search}` : ''}${window.location.hash}`,
    );
  }, []);

  return handoff;
};

export const useHandoffSessionQuery = (code, onRedeemed) =>
  useQuery({
    queryKey: ['handoffSession', code],
    queryFn: async () => {
      const { data: admin } = await AdminApiClient.post(HANDOFF_SESSIONS_PATH, { code });

      onRedeemed?.(admin);

      return admin;
    },
    enabled: !!code,
    retry: false,
    staleTime: Infinity,
    cacheTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

export const useHandoffAdminToken = () => {
  const { code } = useHandoffQuery();
  const { data: admin } = useHandoffSessionQuery(code);

  return admin?.token;
};

export const useCreateHandoffCodeMutation = () =>
  useMutation({
    mutationFn: ({ targetOrigin, redirectTo, target = HANDOFF_TARGETS.ux }) =>
      AdminApiClient.post(HANDOFF_CODES_PATH, { handoffCode: { targetOrigin, redirectTo, target } }).then(
        ({ data }) => data,
      ),
  });
